<script setup>
import { scrollToBenefits } from '@/utils/common';

const { openContactModal } = useContactModal();

const { tp } = useTypograf();

const videoRef = ref(null);
const { setupVideoAutoplay, onVideoError } = useVideoAutoplay();

onMounted(async () => {
  await setupVideoAutoplay(videoRef);
});

const handleVideoError = onVideoError(videoRef);
</script>

<template>
  <section class="section-hero">
    <div class="container">
      <div class="section-hero__inner">
        <div class="section-hero__content">
          <NuxtLink to="/" class="section-hero__link section-hero__link--logo">
            <img
              src="/icons/logo.svg"
              alt=""
              class="section-hero__icon section-hero__icon--logo"
            />
          </NuxtLink>

          <h1 class="section-hero__title">
            <span class="section-hero__font section-hero__font--title">
              {{ tp($t('hero.title')) }}
            </span>
          </h1>

          <p class="section-hero__text">
            <span class="section-hero__font section-hero__font--text">
              {{ tp($t('hero.text')) }}
            </span>
          </p>

          <div class="section-hero__buttons">
            <UiButton
              type="button"
              :text="$t('buttons.touch')"
              class="section-hero__button"
              @click="openContactModal"
            />

            <UiButton
              type="button"
              :text="$t('buttons.benefits')"
              theme="white-outlined"
              class="section-hero__button"
              @click="scrollToBenefits"
            />
          </div>
        </div>

        <video
          ref="videoRef"
          class="section-hero__video"
          poster="/images/video-covers/hero.jpg"
          autoplay
          muted
          loop
          playsinline
          @abort="handleVideoError"
          @suspend="handleVideoError"
          @error.capture="handleVideoError"
        >
          <source src="/videos/hero.mp4" type="video/mp4" />

          <!-- Fallback для старых браузеров -->
          <img src="/images/video-covers/hero.jpg" alt="Hero image" />
        </video>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
$video-desktop-width: 886;

.section-hero {
  &__inner {
    @include media-breakpoint-desktop {
      background: linear-gradient(
        to right,
        $color-main-1 calc(100% - em($video-desktop-width) + 2px),
        transparent calc(100% - em($video-desktop-width) + 2px)
      );

      position: relative;
      width: calc(100% - 1px);

      @include rtl {
        background: linear-gradient(
          to right,
          transparent calc(100% - em($video-desktop-width) - 2px),
          $color-main-1 calc(100% - em($video-desktop-width) - 2px)
        );
        transform: scaleX(-1);
      }
    }
  }

  &__content {
    padding-top: em(65);
    padding-bottom: em(66);
    padding-inline-end: em(450);
    padding-inline-start: em(68);

    @include media-breakpoint-desktop {
      z-index: 2;
      position: relative;

      @include rtl {
        transform: scaleX(-1);
      }
    }

    @include media-breakpoint-mobile {
      background-color: $color-main-1;
      padding-block-start: em(25);
      padding-inline: em(25);
      padding-block-end: em(28);
    }
  }

  &__link {
    &--logo {
      margin-bottom: em(118);
      display: block;

      @include media-breakpoint-mobile {
        margin-bottom: em(32);
      }
    }
  }

  &__icon {
    &--logo {
      width: em(278);
      height: em(98);

      @include media-breakpoint-mobile {
        width: em(152);
        height: em(54);
      }
    }
  }

  &__title {
    margin-bottom: em(69);
    display: inline-block;
    padding-bottom: em(8);

    @include gradient-text(
      linear-gradient(90deg, $color-second-1 27.67%, $color-white 66.36%)
    );

    @include media-breakpoint-mobile {
      margin-bottom: em(18);
      max-width: em(260);
    }
  }

  &__font {
    &--title {
      font-weight: $font-weight-semi-bold;
      font-size: em(80);
      line-height: 1;

      @include media-breakpoint-mobile {
        font-size: em(30);
      }
    }

    &--text {
      font-size: em(25);
      font-weight: $font-weight-light;

      @include media-breakpoint-mobile {
        font-size: em(16);
      }
    }
  }

  &__text {
    margin-bottom: em(131);
    color: $color-white;
    max-width: em(600);

    @include media-breakpoint-mobile {
      margin-bottom: em(37);
    }
  }

  &__buttons {
    display: flex;

    @include media-breakpoint-mobile {
      flex-direction: column;
    }
  }

  &__button {
    & + & {
      @include media-breakpoint-desktop {
        margin-inline-start: em(18);
      }

      @include media-breakpoint-mobile {
        margin-top: em(14);
      }
    }
  }

  &__video {
    object-fit: cover;

    @include media-breakpoint-desktop {
      position: absolute;
      z-index: 1;
      width: em($video-desktop-width);
      height: 100%;
      inset-inline-end: -1px;
      top: 0;
    }

    @include media-breakpoint-mobile {
      width: 100%;
      height: em(345);
    }
  }
}
</style>
